import { ArticleBusinessType, InventoryPool } from 'admin/articleService'
import { ArticleFromApi, CategoryFromApi } from 'products/types'
import { getProductName } from 'products/productTable/utils'
import { TimeSlotGroup } from 'timeSlots/timeSlotsService'
import {
    applicableGroups,
    categoryGroupContext,
    CategoryTimeSlotInfo,
    categoryTimeSlotInfo,
    contextKey,
    GroupContext,
} from 'timeSlots/domain/applicableGroups'
import { CapacitySummary, countingTypeFromWire } from 'capacity/types'
import { visitorKindOf } from 'capacity/api/clientSideGateway'
import { capacityPaths } from 'capacity/paths'
import { CONFIG, FORM_SECTION } from 'capacity/copy'
import { isPassType } from 'capacity/derive/productContext'

export interface SectionCapacityRow {
    key: string
    href: string
    name: string
    detail: string
    pending: boolean
}

export interface ProductSectionInput {
    accountSlug: string
    productId: string
    businessType: ArticleBusinessType | null
    isBundle: boolean
    usedResources: InventoryPool[]
    capacities: CapacitySummary[] | undefined
    categories?: CategoryFromApi[]
    window: 'slot' | 'day' | null
}

// One row per link: a link scoped to a category is a separate charge, so it keeps its own row and says so.
export function productCapacityRows(input: ProductSectionInput): SectionCapacityRow[] {
    const { accountSlug, usedResources, capacities, categories = [], isBundle, window } = input
    const seen = new Set<string>()
    const rows: SectionCapacityRow[] = []
    const links = [...usedResources].sort((a, b) => Number(!!a.productListId) - Number(!!b.productListId))
    for (const link of links) {
        const key = `${link.poolId}:${link.productListId ?? ''}`
        if (seen.has(key)) {
            continue
        }
        seen.add(key)
        const capacity = capacities?.find((entry) => entry.id === link.poolId)
        const name = capacity?.name ?? FORM_SECTION.heading
        const href = capacityPaths.item(accountSlug, link.poolId)
        if (isBundle) {
            rows.push({ key, href, name, detail: FORM_SECTION.bundle, pending: false })
            continue
        }
        const scope = link.productListId
            ? [FORM_SECTION.onlyInCategory(categoryName(categories, link.productListId))]
            : []
        if (capacity && visitorKindOf(capacity)) {
            rows.push({
                key,
                href,
                name,
                detail: [FORM_SECTION.countsAsVisitors(link.amountUsed ?? 1), ...scope].join(' · '),
                pending: false,
            })
            continue
        }
        const parts = [FORM_SECTION.usesOfLimit(link.amountUsed ?? 1), ...scope]
        if (capacity) {
            parts.push(CONFIG.countsShort[countingTypeFromWire(capacity.reservationPeriodType)])
        }
        if (window) {
            parts.push(window === 'slot' ? FORM_SECTION.booksPerSlot : FORM_SECTION.booksWholeDay)
        }
        rows.push({ key, href, name, detail: parts.join(' · '), pending: !window })
    }
    return rows
}

export interface GroupBlock {
    label: string
    groups: TimeSlotGroup[]
}

const categoryName = (categories: CategoryFromApi[], id: string | undefined): string =>
    getProductName(categories.find((category) => category.id === id)?.name) || ''

export function productGroupBlocks(
    product: { id: string; categoryIds: string[] },
    groups: TimeSlotGroup[],
    categories: CategoryFromApi[],
): { blocks: GroupBlock[]; dependsOnCategory: boolean } {
    const info = categoryTimeSlotInfo(categories)
    const resolved = applicableGroups(product, groups, info)
    const blocks: GroupBlock[] = []
    const own = new Set(product.categoryIds)
    const push = (label: string, context: GroupContext) => {
        const existing = blocks.find((block) => block.label === label)
        if (existing) {
            context.groups.forEach((group) => {
                if (!existing.groups.some((entry) => entry.id === group.id)) {
                    existing.groups.push(group)
                }
            })
        } else {
            blocks.push({ label, groups: [...context.groups] })
        }
    }
    for (const context of resolved.contexts) {
        if (context.groups.length === 0) {
            continue
        }
        if (context.scope === 'product') {
            push(FORM_SECTION.setOnProduct, context)
        } else if (context.scope === 'category') {
            const name = context.categoryName || categoryName(categories, context.categoryId)
            push(
                own.has(context.categoryId ?? '')
                    ? FORM_SECTION.fromCategory(name)
                    : FORM_SECTION.fromParentCategory(name),
                context,
            )
        } else {
            push(FORM_SECTION.defaultForAll, context)
        }
    }
    const distinctContexts = new Set(resolved.contexts.map(contextKey))
    return { blocks, dependsOnCategory: distinctContexts.size > 1 }
}

export function categoryGroupBlocks(
    categoryId: string,
    groups: TimeSlotGroup[],
    categories: CategoryFromApi[],
): GroupBlock[] {
    const info: CategoryTimeSlotInfo[] = categoryTimeSlotInfo(categories).map((category) =>
        category.id === categoryId ? { ...category, timeSlotsEnabled: true } : category,
    )
    const context = categoryGroupContext(categoryId, groups, info)
    if (context.groups.length === 0) {
        return []
    }
    if (context.scope === 'category') {
        const label =
            context.categoryId === categoryId
                ? FORM_SECTION.setOnCategory
                : FORM_SECTION.fromParentCategory(context.categoryName || categoryName(categories, context.categoryId))
        return [{ label, groups: context.groups }]
    }
    return [{ label: FORM_SECTION.defaultForAll, groups: context.groups }]
}

// Inactive categories still count as membership: the product keeps their links and time slots.
export const articleCategoryIds = (article: Pick<ArticleFromApi, 'categories' | 'inactiveCategories'>): string[] =>
    [...article.categories, ...(article.inactiveCategories ?? [])].map((category) => category.id)

export const isInCategory = (article: ArticleFromApi, categoryId: string): boolean =>
    articleCategoryIds(article).includes(categoryId)

// Bundles and passes carry links the engine never charges, so they are not "products in a capacity".
const usesCapacity = (article: ArticleFromApi): boolean =>
    article.resources.length > 0 && !article.isBundle && !isPassType(article.businessType)

export function categoryCapacityRows(
    accountSlug: string,
    categoryId: string,
    articles: ArticleFromApi[],
    capacities: CapacitySummary[] | undefined,
): SectionCapacityRow[] {
    const counts = new Map<string, number>()
    for (const article of articles) {
        if (!usesCapacity(article) || !isInCategory(article, categoryId)) {
            continue
        }
        new Set(article.resources.map((resource) => resource.inventoryPoolId)).forEach((poolId) =>
            counts.set(poolId, (counts.get(poolId) ?? 0) + 1),
        )
    }
    return [...counts.entries()]
        .map(([poolId, count]) => ({
            key: poolId,
            href: capacityPaths.item(accountSlug, poolId),
            name: capacities?.find((entry) => entry.id === poolId)?.name ?? FORM_SECTION.heading,
            detail: FORM_SECTION.categoryProducts(count),
            pending: false,
            count,
        }))
        .sort((a, b) => b.count - a.count || a.name.localeCompare(b.name))
}

export function productsLosingTimeSlots(
    categoryId: string,
    articles: ArticleFromApi[],
    groups: TimeSlotGroup[],
    categories: CategoryFromApi[],
): number {
    const info = categoryTimeSlotInfo(categories)
    const on = info.map((c) => (c.id === categoryId ? { ...c, timeSlotsEnabled: true } : c))
    const off = info.map((c) => (c.id === categoryId ? { ...c, timeSlotsEnabled: false } : c))
    return articles.filter((article) => {
        if (!usesCapacity(article) || !isInCategory(article, categoryId)) {
            return false
        }
        const product = { id: article.id, categoryIds: articleCategoryIds(article) }
        return (
            applicableGroups(product, groups, on).window === 'slot' &&
            applicableGroups(product, groups, off).window === 'day'
        )
    }).length
}

export const categoriesAllSwitchedOff = (categoryIds: string[], categories: CategoryFromApi[]): boolean =>
    categoryIds.length > 0 &&
    categoryIds.every((id) => categories.find((category) => category.id === id)?.timeSlotsEnabled === false)
